import { useId, useState } from "react";
import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import type { TimeTrackTarget } from "@shared/timetrack";
import { rankTimeTrackProjects } from "@shared/timetrack-project-ranking";
import { targetBreadcrumb, targetKey } from "./time-utils";

function ProjectThumbnail({ project, label }: { project?: TimeTrackTarget; label: string }) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const source = project?.thumbnail;
  return source && source !== failedSource
    ? <img className="tt-project-thumbnail" src={source} alt="" onError={() => setFailedSource(source)} />
    : <span className="tt-project-thumbnail tt-project-fallback" aria-hidden="true">{label.trim().slice(0, 2).toUpperCase() || "—"}</span>;
}

/** One ranking and thumbnail treatment for timer, attribution and ledger pickers.
 * Radix supplies keyboard navigation, type-ahead and focus restoration.
 */
export function ProjectPicker({ targets, value, onChange, disabled = false, label = "Project",
  placeholder = "Choose a project", emptyLabel, includeWorkItems = false, required = false,
  displayProjectId, displayLabel, disabledProjectIds = [], describedBy, onOpen,
}: {
  targets: TimeTrackTarget[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  emptyLabel?: string;
  includeWorkItems?: boolean;
  required?: boolean;
  displayProjectId?: string;
  displayLabel?: string;
  disabledProjectIds?: string[];
  describedBy?: string;
  onOpen?: () => void;
}) {
  const id = useId();
  const projects = rankTimeTrackProjects(targets.filter(t => t.targetType === "project"));
  const orderedTargets = projects.flatMap(project => includeWorkItems
    ? [project, ...targets.filter(t => t.projectId === project.projectId && t.targetType !== "project")]
    : [project]);
  // A deleted/unavailable current target remains visible, but cannot be selected.
  const selected = includeWorkItems ? targets.find(t => targetKey(t) === value) : projects.find(t => t.projectId === value);
  const project = projects.find(p => p.projectId === (displayProjectId || selected?.projectId));
  const selectedLabel = displayLabel || (selected ? includeWorkItems ? targetBreadcrumb(selected, targets) : selected.label
    : value ? "Current item (not available for reassignment)" : emptyLabel || placeholder);
  const optionValue = (target: TimeTrackTarget) => includeWorkItems ? targetKey(target) : target.projectId;
  // Prefix option values so the optional "all" choice never collides with a project ID.
  return <div className="tt-field">
    <label id={`${id}-label`} htmlFor={id}>{label}</label>
    <Select.Root value={value ? `target:${value}` : emptyLabel ? "all" : ""} required={required}
      disabled={disabled} onOpenChange={open => { if (open) onOpen?.(); }}
      onValueChange={next => onChange(next === "all" ? "" : next.slice(7))}>
      <Select.Trigger id={id} className="tt-project-trigger" aria-labelledby={`${id}-label ${id}-value`} aria-describedby={describedBy}>
        <span className="tt-project-identity">
          {(project || value || displayLabel) && <ProjectThumbnail project={project} label={project?.label || displayLabel || selectedLabel} />}
          <span id={`${id}-value`} className="tt-project-name" title={selectedLabel}>{selectedLabel}</span>
        </span>
        <Select.Icon><ChevronDown size={14} /></Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content className="tt tt-project-menu" position="popper" sideOffset={5}>
          <Select.ScrollUpButton className="tt-project-scroll"><ChevronUp size={14} /></Select.ScrollUpButton>
          <Select.Viewport style={{ padding: 4 }}>
            {emptyLabel && <Select.Item className="tt-project-option" value="all" textValue={emptyLabel}>
              <Select.ItemText>{emptyLabel}</Select.ItemText>
              <Select.ItemIndicator className="tt-project-indicator"><Check size={13} /></Select.ItemIndicator>
            </Select.Item>}
            {value && !selected && <Select.Item className="tt-project-option" value={`target:${value}`} disabled>
              <Select.ItemText>{selectedLabel}</Select.ItemText>
            </Select.Item>}
            {orderedTargets.map(target => {
              const parent = projects.find(p => p.projectId === target.projectId);
              const text = includeWorkItems ? targetBreadcrumb(target, targets) : target.label;
              return <Select.Item key={optionValue(target)} value={`target:${optionValue(target)}`}
                className="tt-project-option" textValue={text} data-project-id={target.projectId} data-target-value={optionValue(target)}
                disabled={disabledProjectIds.includes(target.projectId)}>
                <ProjectThumbnail project={parent} label={parent?.label || target.label} />
                <Select.ItemText>{text}</Select.ItemText>
                <Select.ItemIndicator className="tt-project-indicator"><Check size={13} /></Select.ItemIndicator>
              </Select.Item>;
            })}
            {!orderedTargets.length && <div className="tt-subtle" style={{ padding: 10 }}>No authorized projects available.</div>}
          </Select.Viewport>
          <Select.ScrollDownButton className="tt-project-scroll"><ChevronDown size={14} /></Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  </div>;
}
